import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import PaginationBar from '../components/PaginationBar';
import {
  Search, MapPin, Star, BookOpen, Users, CheckCircle2,
  GraduationCap, Building2, Filter
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;


const typeLabels = {
  university: 'University',
  college: 'College',
  training_provider: 'Training Provider',
};

const typeColors = {
  university: 'bg-blue-100 text-blue-700',
  college: 'bg-emerald-100 text-emerald-700',
  training_provider: 'bg-purple-100 text-purple-700',
};

function StarRating({ rating }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className={`w-3.5 h-3.5 ${s <= Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}`}
        />
      ))}
      <span className="text-xs text-muted-foreground ml-1">{rating?.toFixed(1)}</span>
    </div>
  );
}

export default function UniversitiesPage() {
  const { api } = useAuth();
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [location, setLocation] = useState('');
  const [country, setCountry] = useState('');
  const [degreeLevel, setDegreeLevel] = useState('');
  const [accreditation, setAccreditation] = useState('');
  const [program, setProgram] = useState('');
  const [page, setPage] = useState(1);

  // The endpoint returns every match at once, so pages are cut on the client.
  // Any filter change starts again from the first page.
  useEffect(() => { setPage(1); }, [search, type, location, country, degreeLevel, accreditation, program]);

  useEffect(() => {
    fetchUniversities();
  }, [search, type, location, country, degreeLevel, accreditation, program]);

  const fetchUniversities = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (type) params.type = type;
      if (location) params.location = location;
      if (country) params.country = country;
      if (degreeLevel) params.degree_level = degreeLevel;
      if (accreditation) params.accreditation = accreditation;
      if (program) params.program = program;
      const res = await api.get('/student-services/universities', { params });
      // Handle both array and object response formats
      const unis = Array.isArray(res.data) ? res.data : (res.data?.universities || res.data?.data || []);
      setUniversities(unis);
    } catch (err) {
      console.error('Failed to fetch universities:', err);
      setUniversities([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUniversities();
  };

  const displayUniversities = universities;
  const UNIVERSITIES_PAGE_SIZE = 12;
  const pagedUniversities = displayUniversities.slice((page - 1) * UNIVERSITIES_PAGE_SIZE, page * UNIVERSITIES_PAGE_SIZE);

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-700 to-indigo-700 text-white py-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <GraduationCap className="w-10 h-10 text-white/80" />
            <h1 className="text-4xl font-bold">Browse Universities & Providers</h1>
          </div>
          <p className="text-white/80 text-lg mb-8">
            Discover universities, colleges and training providers across the UK
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="bg-white rounded-2xl p-3 flex flex-col sm:flex-row gap-3 max-w-3xl mx-auto shadow-lg">
            <div className="flex-1 flex items-center gap-2 px-2">
              <Search className="w-5 h-5 text-muted-foreground shrink-0" />
              <input
                type="text"
                placeholder="Search universities, colleges..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 text-gray-800 outline-none text-sm bg-transparent"
              />
            </div>

            <div className="flex gap-2">
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="text-sm text-gray-700 border border-gray-200 rounded-lg px-3 py-2 bg-white outline-none"
              >
                <option value="">All Types</option>
                <option value="university">University</option>
                <option value="college">College</option>
                <option value="training_provider">Training Provider</option>
              </select>
              <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-white">
                <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
                <input
                  type="text"
                  placeholder="Location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="text-sm text-gray-700 outline-none w-24 bg-transparent"
                />
              </div>
              <Button type="submit" className="bg-[#003366] hover:bg-[#003366]/90 text-white rounded-lg px-5">
                Search
              </Button>
            </div>
          </form>

          <div className="flex flex-wrap gap-2 justify-center max-w-3xl mx-auto mt-3">
            <input
              type="text"
              placeholder="Country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="text-sm text-gray-700 border border-gray-200 rounded-lg px-3 py-2 bg-white outline-none w-32"
            />
            <select
              value={degreeLevel}
              onChange={(e) => setDegreeLevel(e.target.value)}
              className="text-sm text-gray-700 border border-gray-200 rounded-lg px-3 py-2 bg-white outline-none"
            >
              <option value="">All Degree Levels</option>
              <option value="foundation">Foundation</option>
              <option value="undergraduate">Undergraduate</option>
              <option value="postgraduate">Postgraduate</option>
              <option value="phd">PhD</option>
            </select>
            <input
              type="text"
              placeholder="Programme (e.g. Computer Science)"
              value={program}
              onChange={(e) => setProgram(e.target.value)}
              className="text-sm text-gray-700 border border-gray-200 rounded-lg px-3 py-2 bg-white outline-none w-52"
            />
            <input
              type="text"
              placeholder="Accreditation"
              value={accreditation}
              onChange={(e) => setAccreditation(e.target.value)}
              className="text-sm text-gray-700 border border-gray-200 rounded-lg px-3 py-2 bg-white outline-none w-36"
            />
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-6xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {loading ? 'Loading...' : `${displayUniversities.length} result${displayUniversities.length !== 1 ? 's' : ''}`}
            </span>
          </div>
          <div className="flex gap-2">
            {type && (
              <Badge
                className="cursor-pointer bg-blue-100 text-blue-700 hover:bg-blue-200"
                onClick={() => setType('')}
              >
                {typeLabels[type]} ×
              </Badge>
            )}
            {location && (
              <Badge
                className="cursor-pointer bg-gray-100 text-gray-700 hover:bg-gray-200"
                onClick={() => setLocation('')}
              >
                {location} ×
              </Badge>
            )}
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            Array(6).fill(0).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-6 space-y-3">
                  <Skeleton className="h-14 w-14 rounded-xl" />
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-10 w-full" />
                </CardContent>
              </Card>
            ))
          ) : displayUniversities.length === 0 ? (
            <div className="col-span-3 text-center py-20">
              <GraduationCap className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No results found</h3>
              <p className="text-muted-foreground">Try adjusting your search or filters.</p>
            </div>
          ) : (
            pagedUniversities.map((uni) => (
              <Card key={uni.id} className="hover:shadow-lg transition-shadow group">
                <CardContent className="p-6">
                  {/* Logo placeholder */}
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center mb-4 group-hover:from-blue-200 group-hover:to-indigo-200 transition-colors">
                    <GraduationCap className="w-7 h-7 text-[#003366]" />
                  </div>

                  {/* Name + badges */}
                  <div className="flex items-start gap-2 mb-2">
                    <h3 className="font-bold text-foreground text-base leading-tight flex-1">{uni.name}</h3>
                    {uni.is_verified && (
                      <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" title="Verified" />
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 mb-3">
                    <Badge className={`text-xs ${typeColors[uni.type] || 'bg-gray-100 text-gray-700'}`}>
                      {typeLabels[uni.type] || uni.type}
                    </Badge>
                  </div>

                  {/* Location */}
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-2">
                    <MapPin className="w-3.5 h-3.5 shrink-0" />
                    {uni.location}
                  </div>

                  {/* Rating */}
                  {uni.average_rating > 0 && (
                    <div className="mb-2">
                      <StarRating rating={uni.average_rating} />
                    </div>
                  )}

                  {/* Description */}
                  <p className="text-xs text-muted-foreground line-clamp-2 mb-4">{uni.description}</p>

                  {/* Stats */}
                  <div className="flex gap-4 text-xs text-muted-foreground mb-4">
                    <span className="flex items-center gap-1">
                      <BookOpen className="w-3.5 h-3.5" />
                      {(uni.programs_count ?? uni.programs?.length ?? uni.course_count ?? 0).toLocaleString()} programs
                    </span>
                    {uni.student_count > 0 && (
                      <span className="flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" />
                        {(uni.student_count || 0).toLocaleString()} students
                      </span>
                    )}
                  </div>

                  <Link to={`/universities/${uni.id}`}>
                    <Button className="w-full bg-[#003366] hover:bg-[#003366]/90 text-white">
                      View University
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {!loading && (
          <PaginationBar page={page} pageSize={UNIVERSITIES_PAGE_SIZE} total={displayUniversities.length} onPageChange={setPage} />
        )}
      </div>
    </div>
  );
}
